import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  UNBILLABLE_REASON_TEXT,
  WITHOUT_CONTRACT_LABEL,
  applicationOperationsMonthLabel,
  billableLines,
  bufferRoom,
  canGiveAway,
  canOpenContract,
  contractLineLabel,
  customerInvoiceLines,
  groupLinesByContract,
  formatDate,
  formatHours,
  formatNumber,
  isBelowUsedHours,
  lastWeekBounds,
  monthBounds,
  projectContractLabel,
  round2,
  type ContractBufferPlan,
  type ContractBufferRoom,
  type ContractLineInvoiceLine,
  type ContractLineTask,
  type CustomersWithPendingHoursResponse,
  type InvoiceBasis,
  type InvoicePreviewResponse,
  type Paginated,
  type UnbilledReviewTasksResponse,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { contractPath, invoicePath } from '@/lib/paths';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Label,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import { Money, PageHeader, PermittedLink, TimeEntryStatusBadge } from '@/components/domain';
import type { ContractListItem, CustomerListItem, InvoiceListItem } from '@/types';

/**
 * Kunden med sine aftaler, som `/customers/:id` svarer.
 *
 * Samme form som CustomerDetail-siden bruger; koblingen kunde -> aftale sker paa serveren, saa
 * de to sider ser de samme aftaler paa den samme kunde.
 */
interface CustomerWithContracts extends CustomerListItem {
  contracts: ContractListItem[];
}

/**
 * F1.9 - sprint-fakturering.
 *
 * Alle tal kommer fra POST /invoices/preview. Frontend regner ikke selv paa timer, satser
 * eller moms: previewet er autoritativt, og oprettelsen bruger praecis samme beregning paa
 * serveren. Derfor kan der ikke opstaa forskel mellem hvad controlleren ser og hvad der
 * faktureres.
 *
 * **Grundlaget er kunden og perioden.** Serveren finder selv kundens vundne aftaler med timer i
 * perioden og laegger hver som sit afsnit i gennemgangen; controlleren vaelger derfra hvilke
 * timer der skal med. Siden vaelger altsaa ikke laengere en aftale eller et grundlag - og
 * dermed kan den kun oprette timefakturaer. Delfaktura, slutfaktura, milepael og abonnement
 * findes stadig i API'et (`invoice_basis` + én `contract_id`), men har ingen indgang i UI.
 */
/** Bufferfeltet venter, til tastningen falder til ro. Samme greb som filtrene paa /kontrakter. */
const BUFFER_DEBOUNCE_MS = 400;

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * Bufferen som tal, eller null naar feltet er tomt eller ulæseligt.
 *
 * **Tomt betyder automatisk**: serveren regner da bufferen af aftalernes samlede buffer - se
 * `planInvoiceBuffer`. Et tal er et beløb i hånden, og 0 er "ingen buffer på denne faktura".
 *
 * Serveren afviser et negativt beløb (zod, `nonnegative`), men feltet skal ikke kunne sende det
 * i første omgang: et fradrag er en anden beslutning end en buffer. Komma accepteres, fordi et
 * dansk tastatur skriver `1.500,50` — punktum er tusindtalsskilletegn her, ikke decimal.
 */
function bufferValue(raw: string): number | null {
  const normalized = raw.trim().replaceAll('.', '').replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/**
 * Et rettet timetal som tal, eller null naar feltet er tomt eller ulæseligt.
 *
 * **Tomt betyder 'ingen retning'** og ikke nul timer: så falder opgaven tilbage på de
 * registrerede timer og bufferens fordeling. Nul er derimod en gyldig retning — 'fakturér ikke
 * denne opgave, men lad den være opkrævet' — og derfor er `>= 0` og ikke `> 0`.
 *
 * Komma accepteres: et dansk tastatur skriver `12,5`. Tusindtalsseparator gør det ikke, i
 * modsætning til `bufferValue` — ingen fakturerer 1.000 timer på én opgave, og `1.5` skal
 * kunne læses som halvanden.
 */
function taskHoursValue(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? round2(parsed) : null;
}

/**
 * Må opgavens timetal rettes?
 *
 * Kun opgaver med et id — retningen stiles til det — og kun de opgaver, der bærer et beløb. En
 * fravalgt, gratis eller scope-dækket opgave tæller ikke i linjens beløb, så et antal på den
 * ville være timer, ingen betaler for. Serveren håndhæver det samme i `applyTaskHours`; feltet
 * findes ikke her, så valget aldrig ser ud til at være der.
 *
 * **Aldrig på en serviceaftale.** Dens antal er de brugte timer, og det er aftalens vilkår — ikke
 * et tal, controlleren vurderer. Serveren ser også bort fra en retning dér.
 */
function canAdjustHours(task: ContractLineTask, line: ContractLineInvoiceLine): boolean {
  if (line.service_agreement) return false;
  return Boolean(task.task_id) && !task.excluded && !task.free && !task.covered_by_scope;
}

export function InvoiceNewPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  /* 'Lav fakturaudkast' does not include the contracts - their names link only with that right. */
  const { user } = useAuth();

  // Kontrakten fra `?contract_id` — sat af knappen "Opret fakturaudkast" paa /kontrakter/:id og
  // af den foreslaaede handling paa en alarm. Laeses én gang; den aendrer sig ikke undervejs.
  const linkedContractId = params.get('contract_id') ?? '';

  const [customerId, setCustomerId] = useState('');
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  /**
   * Ændringsanmodninger sat til gratis.
   *
   * Egen mængde og ikke et flag på `excluded`: de to betyder ikke det samme. Fravalgt er "ikke
   * på denne faktura, kommer igen"; gratis er "faktureret til nul, kommer aldrig igen, og
   * beløbet trækkes fra aftalens buffer".
   */
  const [free, setFree] = useState<Set<string>>(new Set());

  /**
   * Bufferen, som tekst mens der tastes.
   *
   * En streng og ikke et tal: et tomt felt er ikke 0 mens man skriver, og en `number`-state
   * ville gøre feltet umuligt at tømme. Tallet udledes ved kaldet — se `bufferAmount`.
   */
  const [buffer, setBuffer] = useState('');

  /**
   * Beløbet der sendes med. Debounced, fordi hvert tegn ellers ville koste et nyt grundlag:
   * previewet er to Dataverse-kald, et BC-kald og et par forespørgsler mod OnePro.
   */
  const debouncedBuffer = useDebounced(buffer, BUFFER_DEBOUNCE_MS);
  const bufferAmount = bufferValue(debouncedBuffer);

  /**
   * Timer rettet i hånden pr. opgave, som tekst mens der tastes.
   *
   * Strenge og ikke tal, af samme grund som bufferen: et felt, man er ved at tømme, er ikke 0.
   * Et tomt felt står her som `''`, mens der tastes, og bliver til de brugte timer, når feltet
   * forlades - se `finishTaskHours`.
   */
  const [taskHours, setTaskHours] = useState<Record<string, string>>({});

  /**
   * Retningerne der sendes med - øjebliksbilledet af `taskHours`, taget når feltet forlades
   * eller der trykkes Enter. Se `finishTaskHours`.
   *
   * **Ikke debounced.** Et grundlag er to Dataverse-kald, et BC-kald og et par forespørgsler mod
   * OnePro og tager flere sekunder, og med en pause på 400 ms blev det hentet forfra midt i
   * tastningen - én gang pr. tøven. Man retter ét tal ad gangen, så grundlaget beregnes, når
   * tallet er færdigt, og ikke mens det bliver skrevet.
   */
  const [committedTaskHours, setCommittedTaskHours] = useState<Record<string, string>>({});

  /**
   * De læsbare af dem, som serveren vil have dem: id til timer.
   *
   * Et felt, der er halvt skrevet - `12,` - udelades frem for at sende noget, serveren ville
   * afvise. Opgaven står da med sit forrige antal, indtil tallet er færdigt.
   */
  const taskHourOverrides = useMemo(() => {
    const parsed: Record<string, number> = {};
    for (const [taskId, raw] of Object.entries(committedTaskHours)) {
      const hours = taskHoursValue(raw);
      if (hours !== null) parsed[taskId] = hours;
    }
    return parsed;
  }, [committedTaskHours]);

  // Den seneste uge til og med i dag - se lastWeekBounds for hvorfor `end` er i dag og ikke i gaar.
  const defaultPeriod = useMemo(() => lastWeekBounds(new Date()), []);
  const [periodStart, setPeriodStart] = useState(defaultPeriod.start);
  const [periodEnd, setPeriodEnd] = useState(defaultPeriod.end);

  /**
   * Kunderne i trin 1, hentet fra Business Central - de samme som /kunder viser.
   *
   * Kunden er nu **udgangspunktet**: den vaelges foerst, og aftalerne vaelges derefter blandt
   * kundens egne. Én faktura har altid én modtager, ogsaa naar den daekker flere aftaler, og
   * momsen, valutaen og betalingsfristen er kundens.
   *
   * Sorteret paa navn her frem for i kaldet, fordi /customers kun tager `search`, `limit` og
   * `offset`.
   */
  const { data: customers } = useQuery({
    queryKey: ['customers', 'for-invoicing'],
    queryFn: () => api.get<Paginated<CustomerListItem>>('/customers', { limit: 500 }),
  });

  /**
   * Kunderne der har timer klar til fakturering i **perioden** - prikken i vaelgeren nedenfor.
   *
   * Perioden vaelges derfor foerst: prikken hoerer til den, og et svar hentet foer perioden var
   * sat, ville markere de forkerte kunder. Nøglen indeholder begge datoer, saa en rettet periode
   * henter et nyt svar.
   *
   * Serveren afgoer hvem der er markeret - se `customersWithPendingHours`. Koblingen kunde ->
   * timer gaar gennem kundens aftaler og deres tavler (og firmanavnet for timer uden aftale), og
   * den maa ikke ligge to steder: laa en kopi her, kunne prikken og gennemgangen naa hver sin
   * konklusion om den samme kunde.
   *
   * Fejler kaldet, staar listen uden prikker frem for med en fejl. Prikken er en genvej, ikke
   * grundlaget - og en fejlbesked over en kundeliste, der virker fint, ville standse et arbejde,
   * der sagtens kan udfoeres uden den.
   */
  const { data: pendingCustomers } = useQuery({
    queryKey: ['customers', 'pending-hours', periodStart, periodEnd],
    queryFn: () =>
      api.get<CustomersWithPendingHoursResponse>('/customers/pending-hours', {
        period_start: periodStart,
        period_end: periodEnd,
      }),
    enabled: Boolean(periodStart && periodEnd),
    retry: false,
  });

  const withPendingHours = useMemo(() => new Set(pendingCustomers?.data ?? []), [pendingCustomers]);

  const customerOptions = useMemo(
    () =>
      [...(customers?.data ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name, 'da'))
        .map((row) => ({
          value: row.id,
          label: row.bc_customer_no ? `${row.name} (${row.bc_customer_no})` : row.name,
          marked: withPendingHours.has(row.id),
        })),
    [customers, withPendingHours],
  );

  /**
   * Kundens aftaler, hentet med kunden.
   *
   * Kun til tabellen nederst i Grundlag - den er til opslag, ikke til valg: grundlaget bygges
   * af serveren. `/customers/:id` baerer aftalerne allerede, og det er den samme liste som
   * fanen Kontrakter paa /kunder/:id, saa koblingen kunde -> aftale sker ét sted.
   */
  const { data: customer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => api.get<CustomerWithContracts>(`/customers/${customerId}`),
    enabled: Boolean(customerId),
  });

  /**
   * De **aktive** aftaler, sorteret paa kontraktnummer.
   *
   * `active` er statecode 1 Won i Dynamics, altsaa den indgaaede aftale. Numrene sammenlignes
   * numerisk, saa `ONC-102` staar foer `ONC-1011`; aftaler uden nummer staar til sidst.
   */
  const activeContracts = useMemo(
    () =>
      (customer?.contracts ?? [])
        .filter((contract) => contract.status === 'active')
        .sort((a, b) => {
          if (!a.contract_no) return b.contract_no ? 1 : a.title.localeCompare(b.title, 'da');
          if (!b.contract_no) return -1;
          return a.contract_no.localeCompare(b.contract_no, 'da', { numeric: true });
        }),
    [customer],
  );

  /** Fravalgene hoerer til de opgaver, der var i grundlaget. Skifter kunden, gaelder de ikke. */
  const resetSelection = (): void => setExcluded(new Set());

  /**
   * Kroppen til baade preview og oprettelse - den samme, saa det gemte er det viste.
   *
   * **Kun kunden og perioden.** Serveren finder selv alle kundens vundne aftaler med timer i
   * perioden og laegger dem i grundlaget, hver som sit afsnit - se `invoiceResponse` i API'et.
   * Hvad der skal med paa fakturaen, vaelges i gennemgangen ved siden af.
   *
   * `contract_ids` sendes kun, naar linket bad om én bestemt aftale (`?contract_id=`). Da
   * afgraenses grundlaget til den, frem for til alt kunden har.
   */
  const previewInput = {
    /*
     * Kunden udelades, naar den ikke er valgt: et link fra /kontrakter/:id baerer kun aftalen,
     * og serveren finder da kunden ud fra aftalens konto.
     */
    ...(customerId ? { customer_id: customerId } : {}),
    ...(customerId || !linkedContractId ? {} : { contract_ids: [linkedContractId] }),
    invoice_basis: 'hours' as InvoiceBasis,
    period_start: periodStart,
    period_end: periodEnd,
    excluded_task_ids: [...excluded],
    /*
     * Udelades naar ingen er gratis, saa previewets nøgle er den samme som før feltet fandtes -
     * og en tom liste ikke koster et nyt grundlag ved hvert klik andetsteds.
     */
    ...(free.size > 0 ? { free_task_ids: [...free] } : {}),
    /*
     * Udelades naar feltet er tomt: saa regner serveren bufferen af aftalernes samlede buffer.
     * Et skrevet beløb - ogsaa 0 - er controllerens eget.
     */
    ...(bufferAmount !== null ? { buffer_excl_vat: bufferAmount } : {}),
    /*
     * Udelades naar ingen opgave er rettet, saa previewets nøgle er den samme som før feltet
     * fandtes - og et tomt objekt ikke koster et nyt grundlag ved hvert klik andetsteds.
     */
    ...(Object.keys(taskHourOverrides).length > 0 ? { task_hours: taskHourOverrides } : {}),
  };

  /* En kunde er nok - eller aftalen fra et link, som selv peger paa sin kunde. */
  const readyForPreview = Boolean(customerId) || Boolean(linkedContractId);

  const {
    data: preview,
    isFetching,
    isPlaceholderData,
    error: previewError,
  } = useQuery({
    queryKey: ['invoice-preview', previewInput],
    queryFn: () => api.post<InvoicePreviewResponse>('/invoices/preview', previewInput),
    enabled: readyForPreview,
    /**
     * Det forrige grundlag bliver stående, mens det nye beregnes - **men kun for samme kunde og
     * periode.**
     *
     * Et flueben, en buffer eller et rettet timetal ændrer tal i en tabel, man sidder i; uden
     * dette blev hele tabellen skiftet ud med en skeleton ved hvert klik, og feltet man tastede
     * i forsvandt med den. En anden kunde eller periode er derimod et andet grundlag, og at vise
     * det forrige imens ville være at vise den forkerte kundes opgaver.
     */
    placeholderData: (previous, previousQuery) => {
      const before = previousQuery?.queryKey[1] as typeof previewInput | undefined;
      return before?.customer_id === previewInput.customer_id &&
        before?.period_start === previewInput.period_start &&
        before?.period_end === previewInput.period_end
        ? previous
        : undefined;
    },
  });

  /** Tallene på skærmen er det forrige grundlag, og det nye er på vej. Se `placeholderData`. */
  const recalculating = isFetching && isPlaceholderData;

  const create = useMutation({
    mutationFn: () => api.post<InvoiceListItem>('/invoices', previewInput),
    onSuccess: (invoice) => navigate(invoicePath(invoice)),
  });

  /**
   * Kontraktnumrene paa de aftaler, grundlaget har lagt et afsnit for - dem tabellen over 'Sales'
   * og 'Not Billable' slaar op paa.
   *
   * Holdt i sin egen tilstand frem for at blive laest af `preview` direkte: previewet hentes
   * forfra ved hvert flueben, og imens er det `undefined`. Tabellen ville da blinke tom ved hvert
   * klik i gennemgangen. Som tekst, saa et nyt preview med de samme aftaler ikke giver et nyt kald.
   */
  const [reviewContractNos, setReviewContractNos] = useState('');
  useEffect(() => {
    if (!preview) return;
    setReviewContractNos(
      preview.contracts
        .map((contract) => contract.contract_no?.trim())
        .filter((no): no is string => Boolean(no))
        .join(','),
    );
  }, [preview]);

  const toggleTask = (taskId: string | null): void => {
    if (!taskId) return;
    const next = new Set(excluded);
    if (next.has(taskId)) next.delete(taskId);
    else next.add(taskId);
    setExcluded(next);
  };

  /**
   * Retter timetallet på én opgave, mens der tastes.
   *
   * Et tomt felt bliver stående tomt - man skal kunne slette tallet og skrive et nyt uden at det
   * gamle springer tilbage. Hvad det tomme felt betyder, afgøres først, når feltet forlades.
   */
  const setTaskHoursFor = (taskId: string | null, raw: string): void => {
    if (!taskId) return;
    setTaskHours((current) => ({ ...current, [taskId]: raw }));
  };

  /**
   * Feltet forlades (Tab, klik andetsteds eller Enter): et tomt felt bliver til de brugte timer,
   * og grundlaget beregnes med det, der står.
   *
   * **Tomt er ikke et slutresultat.** Det siger ikke, hvad der faktureres, og det må ikke blive
   * til nul. Det falder tilbage på det, der er brugt på opgaven - tallet 'Brugt' under feltet.
   *
   * Er de brugte timer allerede grundlagets antal - ingen buffer på opgaven, og ingen tidligere
   * retning - fjernes retningen helt: feltet viser det samme, og en retning til samme værdi ville
   * koste et nyt grundlag for ingenting. Ellers bliver de brugte timer retningen, så feltet ikke
   * viser et andet tal end det, der faktureres.
   */
  const finishTaskHours = (task: ContractLineTask, usedHours: number): void => {
    const taskId = task.task_id;
    if (!taskId) return;

    const next = { ...taskHours };
    if (next[taskId]?.trim() === '') {
      const alreadyUsed =
        taskHoursValue(committedTaskHours[taskId] ?? '') === null &&
        round2(usedHours) === round2(task.quantity);

      if (alreadyUsed) delete next[taskId];
      else next[taskId] = formatHours(usedHours);
    }

    setTaskHours(next);
    setCommittedTaskHours(next);
  };

  /**
   * Gratis: faktureres til 0 kr og trækkes fra aftalens buffer.
   *
   * Kun på ændringsanmodninger. Serveren håndhæver det — se `groupHoursByContractLine` — men
   * knappen findes heller ikke på de øvrige rækker, så valget aldrig ser ud til at være der.
   */
  const toggleFree = (taskId: string | null): void => {
    if (!taskId) return;
    const next = new Set(free);
    if (next.has(taskId)) next.delete(taskId);
    else next.add(taskId);
    setFree(next);
  };

  /**
   * Til- og fravalg af **hele** kontraktlinjen i ét klik.
   *
   * En linje kan have mange opgaver under sig, og at klikke dem igennem én for én er den slags
   * arbejde, der bliver gjort halvt. `include` sendes med frem for at vende hver enkelt: er
   * linjen delvist valgt, skal ét klik gøre den entydig - ikke bytte om paa hvad der er valgt.
   */
  const setLineIncluded = (taskIds: readonly string[], include: boolean): void => {
    const next = new Set(excluded);
    for (const id of taskIds) {
      if (include) next.delete(id);
      else next.add(id);
    }
    setExcluded(next);
  };

  const unbillableHours = round2(
    (preview?.unbillable ?? []).reduce((sum, item) => sum + item.duration_hours, 0),
  );

  /**
   * Hvor meget hver aftale har tilbage at give væk af.
   *
   * 'Gratis' er ikke en gave af huset — beløbet trækkes af aftalens buffer. Er der ikke dækning,
   * vises knappen ikke: en knap, der kun kan føre til en afvist faktura, er værre end ingen knap.
   *
   * `bufferRoom` fra packages/shared og ikke et regnestykke her: serveren afviser oprettelsen med
   * præcis den samme funktion, og regnede de to hver for sig, kunne knappen tilbyde noget, der
   * blev afvist et klik senere. Loftet pr. aftale kommer fra previewet — se
   * `InvoicePreviewContract.buffer_excl_vat`.
   */
  const bufferRoomByContract = useMemo(() => {
    if (!preview) return new Map<string, ContractBufferRoom>();

    const available = new Map(
      preview.contracts.map((contract) => [contract.id, contract.buffer_excl_vat]),
    );
    return new Map(
      bufferRoom(preview.lines, available).map((row) => [row.contract?.id ?? 'none', row]),
    );
  }, [preview]);

  /**
   * Linjerne som de faktisk faktureres - uden de fravalgte opgaver, og uden de linjer der intet
   * har tilbage.
   *
   * `billableLines` fra packages/shared og ikke en optaelling her: det er praecis den funktion,
   * serveren skiller det viste fra det sendte med, foer fakturaen oprettes i BC. Regnede de to
   * hver for sig, kunne 'Antal linjer' love noget andet end det, der blev gemt.
   */
  const chosenLines = preview ? billableLines(preview.lines) : [];

  /**
   * Application operations for the month, when the invoice is created on the 1st - the server
   * looks them up and puts them in the totals. They are not chosen here: the month is billed once,
   * on whichever invoice comes first.
   */
  const operationsLines = preview?.application_operations.lines ?? [];

  /**
   * The customer's retainer - a fixed fee in place of the hours. The hours below are then priced
   * at 0 kr by the server; choosing them decides which tasks are marked as invoiced.
   */
  const retainer = preview?.retainer ?? null;

  /**
   * Er der overhovedet noget at opkræve?
   *
   * Ikke det samme som at der er linjer. En linje kan stå på nul kroner, fordi alt på den er
   * gratis eller inden for scope — den bliver gemt, så bilaget har timerne, men kunden får
   * ingen regning af den. `customerInvoiceLines` er præcis den funktion, serveren bygger BC's
   * linjer med, så knappen her og 422'eren derovre er enige om, hvornår der er en faktura.
   * Applikationsdriften og en retainer er altid en regning.
   */
  const canInvoice =
    customerInvoiceLines(chosenLines).length > 0 || operationsLines.length > 0 || retainer !== null;

  /**
   * Serviceaftalerne på fakturaen. Bufferen lægges ikke på dem — serveren springer deres linjer
   * over, se `planInvoiceBuffer` — og feltet skal sige det, før nogen skriver et beløb, der
   * alligevel ikke lander. Har fakturaen kun serviceaftaler, er feltet slået fra.
   */
  const hasServiceLines = Boolean(preview?.lines.some((line) => line.service_agreement));
  const onlyServiceLines =
    hasServiceLines && Boolean(preview?.lines.every((line) => line.service_agreement));

  return (
    <>
      <PageHeader
        title="Nyt fakturaudkast"
        description="Timer grupperes pr. opgave. Kunden ser kun opgavetitlen."
      />

      {/*
        `items-start` og ikke standardens stretch.
        Gitterets raekke er lige saa hoej som sit hoejeste element - gennemgangen til hoejre - og
        uden dette blev Grundlag straekket med, saa kortet fik et tomt felt under den interne
        note, der voksede med hver linje i grundlaget. Med start beholder hvert kort sin egen
        hoejde. Samme greb som paa /fakturering/:id.
      */}
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>1. Grundlag</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              {/*
              Trin ét: perioden. Den staar foerst, fordi den afgoer hvilke kunder der har noget at
              fakturere - og det er praecis den oplysning, prikken i kundevaelgeren nedenfor
              baerer. Vaelger man kunden foerst, er prikken sat paa en periode, man ikke har taget
              stilling til endnu.
            */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="from">Periode fra</Label>
                  <Input
                    id="from"
                    type="date"
                    value={periodStart}
                    onChange={(event) => setPeriodStart(event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="to">Periode til</Label>
                  <Input
                    id="to"
                    type="date"
                    value={periodEnd}
                    onChange={(event) => setPeriodEnd(event.target.value)}
                  />
                </div>
              </div>

              {/*
              Trin to: kunden. Fakturaens modtager, og rammen om hvilke aftaler der kan vaelges.

              Soegbar dropdown og ikke en <select>: en BC-tenant har hundreder af kunder, og en
              prik kan ikke tegnes inde i en <option> - browseren viser kun tekst der. Prikken er
              hele pointen, saa feltet er den samme vaelger som filtrene paa /kontrakter og /timer.
            */}
              <div>
                {/* Ikke en <Label>: vaelgeren er en knap og et panel, ikke ét felt at pege paa - samme
                  greb som `FilterField` bruger paa /timer og /kontrakter. */}
                <span className="field-label">Kunde</span>
                <Combobox
                  value={customerId}
                  onChange={(next) => {
                    setCustomerId(next);
                    /* Aftalerne hoerte til den forrige kunde, og fravalgene til dens opgaver. */
                    resetSelection();
                    setReviewContractNos('');
                  }}
                  options={customerOptions}
                  allLabel="Vælg kunde"
                  searchPlaceholder="Søg på navn eller nummer"
                  markLabel="Har timer klar til fakturering i perioden"
                />
                <p className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                  Kunden har timer i perioden, der er markeret til fakturering i OnePro.
                </p>
              </div>

              {/*
              Kundens aktive aftaler, til opslag.
              Ikke et valg: grundlaget bygges af serveren, og tabellen findes for at kunne
              sammenholde det med, hvad kunden faktisk har. En aftale, der staar her men ikke i
              gennemgangen, har ingen timer klar til fakturering i perioden.
            */}
              {customerId ? (
                <div className="border-t border-line pt-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Aktive kontrakter
                  </p>

                  {!customer ? (
                    <Skeleton className="h-12" />
                  ) : activeContracts.length === 0 ? (
                    <p className="text-xs text-ink-muted">
                      Kunden har ingen vundne aftaler i Dynamics.
                    </p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-line text-ink-muted">
                          <th className="w-[90px] py-1 text-left font-medium">Nr.</th>
                          <th className="py-1 text-left font-medium">Kontrakt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeContracts.map((contract) => (
                          <tr key={contract.id} className="border-b border-line/60">
                            <td className="py-1 tabular text-ink-muted">
                              {contract.contract_no ?? '—'}
                            </td>
                            <td className="py-1">
                              <PermittedLink
                                to={contractPath(contract)}
                                allowed={canOpenContract(user, contract)}
                                className="hover:underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {contract.title}
                              </PermittedLink>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : null}
            </CardBody>
          </Card>

          {readyForPreview ? (
            <UnbilledReviewCard
              contractNos={reviewContractNos}
              loading={isFetching && !reviewContractNos}
            />
          ) : null}
        </div>

        <div className="space-y-4 lg:col-span-2">
          {/*
            Bufferen. Står over gennemgangen, fordi den ændrer de linjer, gennemgangen viser —
            beløbet lægges ud på opgavernes antal, så kunden ser et lidt højere timetal og ingen
            bufferlinje. Selve fordelingen sker på serveren (`planInvoiceBuffer`), så previewet,
            den oprettede faktura og den automatiske fakturering ikke kan nå hvert sit tal.

            Tomt felt er automatisk: hver aftale tager sin andel af det, der mangler af dens
            samlede buffer, og ingen opgave kommer over sit estimat.
          */}
          <Card>
            <CardHeader>
              <CardTitle>Buffer</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-40">
                  <Label htmlFor="buffer">Beløb (DKK)</Label>
                  <Input
                    id="buffer"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={buffer}
                    onChange={(event) => setBuffer(event.target.value)}
                    placeholder={retainer ? 'Retainer' : 'Automatisk'}
                    /* A retainer replaces the hours, so there are none to lay a buffer on. */
                    disabled={onlyServiceLines || retainer !== null}
                  />
                </div>
                {buffer.trim() ? (
                  <Button variant="ghost" size="sm" onClick={() => setBuffer('')}>
                    Brug automatisk
                  </Button>
                ) : null}
                {/*
                  Bufferen fakturaen faktisk bærer, og **ikke** det indtastede.

                  De to kan afvige af to grunde nu. Antallet på en linje afrundes til to
                  decimaler, så de kan ligge et par ører fra hinanden — og en opgave, hvis timer
                  er rettet i hånden nedenfor, ændrer summen uden at røre feltet. Tallet her er
                  regnet af linjerne selv (`derivedBuffer`), så det er ét tal uanset hvilken vej
                  bufferen kom, og det er det, der lægges på aftalen.
                */}
                {preview?.buffer && preview.buffer.applied_excl_vat !== 0 ? (
                  <p className="text-xs text-ink-muted">
                    Lagt på linjerne: <Money value={preview.buffer.applied_excl_vat} />
                    {Object.keys(taskHourOverrides).length > 0 ? (
                      <span className="ml-1">
                        (inkl. {Object.keys(taskHourOverrides).length} rettet
                        {Object.keys(taskHourOverrides).length === 1 ? ' opgave' : 'e opgaver'})
                      </span>
                    ) : null}
                  </p>
                ) : null}
              </div>
              {preview && preview.buffer.plans.some((plan) => plan.contract) ? (
                <BufferPlanTable
                  plans={preview.buffer.plans}
                  mode={preview.buffer.mode}
                  currency={preview.customer.currency}
                />
              ) : null}
              <p className="text-xs text-ink-muted">
                Kun til intern brug. Tomt felt er automatisk: hver aftale tager sin andel af det,
                der mangler af dens samlede buffer, fordelt over de timer, aftalen har tilbage. Et
                beløb i feltet fordeles i stedet — 0 er ingen buffer. Bufferen lægges på opgaverne
                efter deres timer, men aldrig over en opgaves estimat, og en opgave uden estimat
                eller over sit estimat får ingenting. Timerne i grundlaget nedenfor er de
                registrerede — forskellen til linjens antal er bufferen. Passer fordelingen ikke på
                en enkelt opgave, kan dens timer rettes i feltet <em>Faktureres</em> nedenfor; så
                går bufferen til de andre.
              </p>
              {hasServiceLines ? (
                <p className="text-xs text-ink-muted">
                  {onlyServiceLines
                    ? 'Fakturaen har kun serviceaftaler, og de kan ikke få en buffer. '
                    : 'Bufferen fordeles kun på projektaftalerne — serviceaftaler får ingen buffer. '}
                  En serviceaftale faktureres med de brugte timer, og timerne kan ikke rettes.
                </p>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="flex items-center justify-between gap-3">
              <CardTitle>2. Gennemgå grundlaget</CardTitle>
              {/*
                Tabellen bliver stående, mens grundlaget beregnes forfra - se `placeholderData`.
                Mærkatet siger, at tallene i den er de forrige, indtil de nye er der.
              */}
              {recalculating ? (
                <span className="text-xs text-ink-muted" role="status">
                  Beregner grundlaget…
                </span>
              ) : null}
            </CardHeader>

            {!readyForPreview ? (
              <CardBody>
                <p className="text-sm text-ink-muted">
                  Vælg en kunde. Timer, som OnePro har markeret til fakturering i perioden, kommer
                  frem her fordelt på kundens aftaler, og du vælger hvilke der skal på fakturaen.
                </p>
              </CardBody>
            ) : isFetching && !preview ? (
              <CardBody>
                <Skeleton className="h-32" />
              </CardBody>
            ) : previewError ? (
              <CardBody>
                <Alert tone="error">
                  {previewError instanceof ApiError
                    ? previewError.message
                    : 'Kunne ikke beregne grundlaget.'}
                </Alert>
              </CardBody>
            ) : preview ? (
              <>
                {preview.blockers.length > 0 ? (
                  <div className="px-4 pt-4">
                    <Alert tone="error" title="Fakturaen kan ikke oprettes">
                      <ul className="list-disc pl-4">
                        {preview.blockers.map((blocker) => (
                          <li key={blocker}>{blocker}</li>
                        ))}
                      </ul>
                    </Alert>
                  </div>
                ) : null}

                {preview.warnings.length > 0 ? (
                  <div className="px-4 pt-4">
                    <Alert tone="warning">
                      <ul className="list-disc pl-4">
                        {preview.warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </Alert>
                  </div>
                ) : null}

                {/*
                  Kundens retainer: én linje i stedet for timerne. Timerne står stadig nedenfor -
                  til 0 kr - så det kan vælges, hvilke opgaver der markeres som faktureret.
                */}
                {retainer ? (
                  <>
                    <div className="px-4 pt-4">
                      <Alert tone="info">
                        Kunden har en fast retainer. Fakturaen får kun retainerlinjen; timerne
                        nedenfor markeres som faktureret, men koster ikke ekstra.
                      </Alert>
                    </div>
                    <Table>
                      <thead>
                        <tr>
                          <Th className="w-8" />
                          <Th>Retainer</Th>
                          <Th className="w-[100px] text-right">Antal</Th>
                          <Th className="w-[130px] text-right">Enhedspris</Th>
                          <Th className="w-[140px] text-right">Total</Th>
                        </tr>
                      </thead>
                      <tbody>
                        <Tr>
                          <Td />
                          <Td>
                            <span className="font-medium">{retainer.description}</span>
                            <span className="mt-0.5 block text-xs text-ink-muted">
                              {retainer.bc_item_no
                                ? `Vare ${retainer.bc_item_no} i Business Central`
                                : 'Varen Udviklingstimer i Business Central'}
                            </span>
                          </Td>
                          <Td className="text-right tabular">
                            {formatNumber(
                              retainer.quantity,
                              Number.isInteger(retainer.quantity) ? 0 : 2,
                            )}
                          </Td>
                          <Td className="text-right">
                            <Money
                              value={retainer.unit_price_excl_vat}
                              currency={preview.customer.currency}
                            />
                          </Td>
                          <Td className="text-right">
                            <Money
                              value={retainer.total_excl_vat}
                              currency={preview.customer.currency}
                            />
                          </Td>
                        </Tr>
                      </tbody>
                    </Table>
                  </>
                ) : null}

                {preview.lines.length > 0 ? (
                  <Table>
                    <thead>
                      <tr>
                        <Th className="w-8" />
                        <Th>Linje</Th>
                        <Th className="w-[100px] text-right">Antal</Th>
                        <Th className="w-[130px] text-right">Enhedspris</Th>
                        <Th className="w-[140px] text-right">Total</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {/*
                        Grupperet pr. aftale. Ét afsnit pr. "Kontraktoplysninger", saa det er
                        tydeligt hvilke timer der faktureres paa hvilken aftale - samme opdeling
                        som previewet paa /fakturering/:id viser bagefter.

                        Overskriften vises kun, naar der *er* mere end én gruppe: med én ville
                        den gentage det, kortet til venstre allerede siger.
                      */}
                      {groupLinesByContract(preview.lines).map((byContract) => (
                        <Fragment key={byContract.contract?.id ?? 'none'}>
                          {byContract.known ? (
                            <Tr>
                              <Td colSpan={5} className="bg-canvas">
                                <span className="text-xs uppercase tracking-wide text-ink-muted">
                                  Kontraktoplysninger
                                </span>
                                <span className="mt-0.5 block text-sm font-semibold">
                                  {byContract.contract
                                    ? [byContract.contract.contract_no, byContract.contract.title]
                                        .filter(Boolean)
                                        .join(' · ')
                                    : WITHOUT_CONTRACT_LABEL}
                                </span>
                              </Td>
                            </Tr>
                          ) : null}

                          {byContract.rows.map((line, index) => {
                            /*
                             * Linjens flueben daekker alle opgaver under den. Tre tilstande, som en
                             * mappe med filer: alle valgt, ingen valgt, eller noget imellem - det
                             * sidste vises som `indeterminate`, saa halvt valgt ikke ligner helt
                             * valgt. Ét klik gør linjen entydig: er den ikke helt valgt, vaelges alt.
                             */
                            const taskIds = line.tasks
                              .map((task) => task.task_id)
                              .filter((id): id is string => id !== null);
                            const chosen = taskIds.filter((id) => !excluded.has(id)).length;
                            const allChosen = taskIds.length > 0 && chosen === taskIds.length;
                            const someChosen = chosen > 0 && !allChosen;

                            return (
                              <Fragment key={`${line.contract_line ?? 'none'}-${index}`}>
                                <Tr>
                                  <Td>
                                    {taskIds.length > 0 ? (
                                      <input
                                        type="checkbox"
                                        aria-label={`Medtag alle opgaver på ${line.description}`}
                                        checked={allChosen}
                                        // React har ingen prop til indeterminate - den saettes paa noden.
                                        ref={(node) => {
                                          if (node) node.indeterminate = someChosen;
                                        }}
                                        onChange={() => setLineIncluded(taskIds, !allChosen)}
                                      />
                                    ) : null}
                                  </Td>
                                  <Td className="font-medium">{line.description}</Td>
                                  <Td className="text-right tabular">
                                    {line.unit === 'hours'
                                      ? formatHours(line.quantity)
                                      : line.quantity}
                                  </Td>
                                  <Td className="text-right">
                                    <Money
                                      value={line.unit_price_excl_vat}
                                      currency={preview.customer.currency}
                                    />
                                  </Td>
                                  <Td className="text-right">
                                    <Money
                                      value={line.total_excl_vat}
                                      currency={preview.customer.currency}
                                    />
                                  </Td>
                                </Tr>

                                {/*
                                 * Opgaverne under kontraktlinjen. Kun for timegrundlaget: et beloeb
                                 * har ingen opgaver bag sig, og linjen staar da alene.
                                 *
                                 * Og kun naar de siger noget nyt. Uden en kontrakt er linjen selv en
                                 * opgave - se `groupHoursByTask` - og en underraekke med samme titel
                                 * ville staa som en gentagelse. Fluebenet paa linjen daekker den ene
                                 * opgave, saa den kan stadig vaelges fra.
                                 */}
                                {line.tasks.length > 0 &&
                                !(
                                  line.tasks.length === 1 &&
                                  line.tasks[0]?.title === line.description
                                )
                                  ? line.tasks.map((task) => {
                                      /* Falder tilbage paa det opkraevede, naar kilden ikke kender de to hver for sig. */
                                      const usedHours = task.used_hours ?? task.quantity;
                                      const estimate = task.estimate_hours ?? null;
                                      const adjustable = canAdjustHours(task, line);
                                      /*
                                       * Tallet i feltet lige nu - ogsaa foer det er sendt.
                                       * Grundlaget beregnes foerst, naar feltet forlades (se
                                       * `finishTaskHours`), og advarslerne skal foelge
                                       * tastningen frem for at vente paa det. Et tomt felt
                                       * regnes som de brugte timer, som det bliver til; et halvt
                                       * skrevet tal falder tilbage paa grundlagets antal.
                                       */
                                      const draft = task.task_id
                                        ? taskHours[task.task_id]
                                        : undefined;
                                      const shownQuantity =
                                        draft === undefined
                                          ? task.quantity
                                          : draft.trim() === ''
                                            ? usedHours
                                            : (taskHoursValue(draft) ?? task.quantity);
                                      /*
                                       * Samme regel som `tasksOverEstimate` paa serveren: kun de
                                       * opgaver, der baerer et beloeb, og kun naar der *er* et
                                       * estimat. 'Intet estimat' er ikke nul timer.
                                       */
                                      const overEstimate =
                                        adjustable &&
                                        estimate !== null &&
                                        estimate > 0 &&
                                        shownQuantity > estimate;
                                      /*
                                       * Og modstykket: faerre timer end der er brugt. Reglen er
                                       * `isBelowUsedHours` fra packages/shared - den samme, som
                                       * serverens advarsel (`tasksBelowUsed`) bygger paa.
                                       */
                                      const belowUsed =
                                        adjustable &&
                                        isBelowUsedHours({
                                          quantity: shownQuantity,
                                          used_hours: usedHours,
                                        });

                                      return (
                                        <Tr
                                          key={task.task_id ?? `${task.title}-${line.description}`}
                                        >
                                          <Td>
                                            <input
                                              type="checkbox"
                                              aria-label={`Medtag ${task.title}`}
                                              checked={!task.task_id || !excluded.has(task.task_id)}
                                              onChange={() => toggleTask(task.task_id)}
                                            />
                                          </Td>
                                          {/*
                                           * En fravalgt opgave bliver staaende, gennemstreget: den er
                                           * ikke med paa fakturaen, men fluebenet skal kunne saettes igen.
                                           */}
                                          <Td
                                            className={`pl-6 text-xs ${
                                              task.excluded
                                                ? 'text-ink-muted line-through'
                                                : 'text-ink-muted'
                                            }`}
                                          >
                                            {task.title}
                                            {/*
                                            Service agreement work done on a project's board: which
                                            project it was. The bilag shows it as 'Kontrakt'.
                                          */}
                                            {task.project_contract ? (
                                              <span
                                                className="ml-2 text-[11px] text-ink-muted"
                                                title="Opgaven er markeret Serviceaftale i OnePro og faktureres på serviceaftalen, men arbejdet er udført på denne aftales tavle."
                                              >
                                                · {projectContractLabel(task.project_contract)}
                                              </span>
                                            ) : null}
                                            {/*
                                            Ændring inden for scope. Ikke et valg, men en oplysning:
                                            Orchestrator har klassificeret anmodningen som noget
                                            andet end 'Uden for scope', og så er arbejdet betalt af
                                            kontraktsummen. Mærkatet står, fordi rækken ellers ville
                                            se ud som en opgave, der bare ikke blev talt med.
                                          */}
                                            {task.covered_by_scope && !task.excluded ? (
                                              <span
                                                className="ml-2 rounded border border-line bg-surface-2 px-1.5 py-0.5 text-[11px] text-ink-muted"
                                                title="Ændringen er inden for det aftalte scope og opkræves ikke. Den står på bilaget med «(I scope)» i stedet for timer. Skal den faktureres, skal anmodningen klassificeres 'Uden for scope' i Orchestrator."
                                              >
                                                I scope
                                              </span>
                                            ) : null}
                                            {/*
                                            Hele betingelsen står i `canGiveAway` i
                                            packages/shared — hvilke opgaver der må gives væk, og
                                            om aftalens buffer kan bære denne.

                                            Den regner på det, der er tilbage, når de øvrige
                                            'Gratis' på aftalen er trukket fra, så to ændringer,
                                            der hver for sig er inden for bufferen men tilsammen
                                            ikke er, kun kan vælges én af. Serveren afviser det
                                            samme med `overdrawnBuffers`; knappen findes for, at
                                            man ikke først skal trykke Gem for at få det at vide.
                                          */}
                                            {canGiveAway(
                                              task,
                                              line.unit_price_excl_vat,
                                              bufferRoomByContract.get(line.contract?.id ?? 'none'),
                                            ) ? (
                                              <button
                                                type="button"
                                                className={`ml-2 rounded border px-1.5 py-0.5 text-[11px] ${
                                                  task.free
                                                    ? 'border-brand bg-brand-50 font-medium text-brand-800'
                                                    : 'border-line text-ink-muted hover:text-ink'
                                                }`}
                                                aria-pressed={task.free}
                                                title={
                                                  task.free
                                                    ? 'Faktureres til 0 kr og trækkes fra aftalens buffer. Klik for at fakturere den normalt.'
                                                    : 'Gør ændringen gratis for kunden. Beløbet trækkes fra aftalens buffer.'
                                                }
                                                onClick={() => toggleFree(task.task_id)}
                                              >
                                                Gratis
                                              </button>
                                            ) : null}
                                          </Td>
                                          {/*
                                          Tre tal pr. opgave, og det er med vilje alle tre.

                                          `Faktureres` er det, kunden betaler for - og det ene af
                                          dem, der kan rettes. Bufferen ovenfor fordeler på alle
                                          linjer; her kan den enkelte opgave sættes, når den
                                          fordeling ikke passer - fx fordi den bringer opgaven over
                                          sit estimat. Rettes et felt, flytter bufferen sig i
                                          kortet foroven: den er forskellen mellem de to tal.

                                          `Brugt` og `Est.` står under feltet, fordi de er det, man
                                          skal vurdere tallet imod. Uden estimatet kunne man ikke se
                                          overskridelsen, og uden de brugte timer ikke se, hvor
                                          meget buffer der allerede er lagt på.
                                        */}
                                          <Td className="text-right">
                                            {adjustable ? (
                                              <input
                                                type="text"
                                                inputMode="decimal"
                                                aria-label={`Timer der faktureres for ${task.title}`}
                                                className={`h-7 w-20 rounded border bg-surface px-2 text-right text-xs tabular ${
                                                  overEstimate || belowUsed
                                                    ? 'border-warning text-warning'
                                                    : 'border-line'
                                                }`}
                                                value={draft ?? formatHours(task.quantity)}
                                                onChange={(event) =>
                                                  setTaskHoursFor(task.task_id, event.target.value)
                                                }
                                                /*
                                                 * Grundlaget beregnes, naar tallet er faerdigt: feltet
                                                 * forlades, eller der trykkes Enter - som saa forlader
                                                 * det, saa der kun er én vej ind. Et tomt felt bliver
                                                 * dér til de brugte timer.
                                                 */
                                                onBlur={() => finishTaskHours(task, usedHours)}
                                                onKeyDown={(event) => {
                                                  if (event.key === 'Enter')
                                                    event.currentTarget.blur();
                                                }}
                                              />
                                            ) : (
                                              <span className="text-xs tabular text-ink-muted">
                                                {formatHours(task.quantity)}
                                              </span>
                                            )}
                                            <span className="mt-0.5 block text-[11px] text-ink-muted">
                                              Brugt {formatHours(usedHours)} · Est.{' '}
                                              {task.estimate_hours === null ||
                                              task.estimate_hours === undefined
                                                ? '—'
                                                : formatHours(task.estimate_hours)}
                                            </span>
                                            {/*
                                            Kun en advarsel. Det er ikke forbudt at fakturere over
                                            et estimat - serveren skriver det samme i `warnings` og
                                            blokerer ikke - men det skal være set.
                                          */}
                                            {overEstimate ? (
                                              <span className="mt-0.5 block text-[11px] text-warning">
                                                Over estimat
                                              </span>
                                            ) : null}
                                            {/*
                                              Samme slags advarsel: det kan vaere bevidst at lade
                                              kunden slippe for nogle timer, men forskellen er
                                              arbejde, der ikke bliver betalt.
                                            */}
                                            {belowUsed ? (
                                              <span
                                                className="mt-0.5 block text-[11px] text-warning"
                                                title="Der faktureres færre timer, end der er brugt på opgaven. Forskellen bliver ikke betalt."
                                              >
                                                Under brugte timer
                                              </span>
                                            ) : null}
                                          </Td>
                                          <Td />
                                          <Td className="text-right text-xs text-ink-muted">
                                            {task.free || task.covered_by_scope ? '0 kr' : null}
                                          </Td>
                                        </Tr>
                                      );
                                    })
                                  : null}
                              </Fragment>
                            );
                          })}
                        </Fragment>
                      ))}
                    </tbody>
                  </Table>
                ) : null}

                {/*
                  Abonnementerne: efter timerne. Applikationsdrift og apps på aftalen i CRM, én
                  linje pr. produkt til varens pris i Business Central - og abonnementerne fra
                  /fakturering/abonnementer, når en periode begynder i måneden. Kun på en faktura,
                  der oprettes den 1.
                */}
                {operationsLines.length > 0 && preview.application_operations.month ? (
                  <Table>
                    <thead>
                      <tr>
                        <Th className="w-8" />
                        <Th>
                          Abonnementer for{' '}
                          {applicationOperationsMonthLabel(preview.application_operations.month)}
                        </Th>
                        <Th className="w-[100px] text-right">Antal</Th>
                        <Th className="w-[130px] text-right">Enhedspris</Th>
                        <Th className="w-[140px] text-right">Total</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {operationsLines.map((line) => (
                        <Tr key={`${line.contract?.id ?? line.subscription_id}-${line.product}`}>
                          <Td />
                          <Td>
                            <span className="font-medium">{line.description}</span>
                            <span className="mt-0.5 block text-xs text-ink-muted">
                              {line.subscription_id
                                ? [
                                    ...(line.contract ? [contractLineLabel(line.contract)] : []),
                                    (line.months ?? 1) > 1
                                      ? `Abonnement · ${line.months} måneder forud`
                                      : 'Abonnement · pris pr. måned',
                                  ].join(' · ')
                                : `${contractLineLabel(line.contract)} · pris fra Business Central`}
                            </span>
                          </Td>
                          <Td className="text-right tabular">
                            {formatNumber(line.quantity, Number.isInteger(line.quantity) ? 0 : 2)}
                          </Td>
                          <Td className="text-right">
                            <Money
                              value={line.unit_price_excl_vat}
                              currency={preview.customer.currency}
                            />
                          </Td>
                          <Td className="text-right">
                            <Money
                              value={line.total_excl_vat}
                              currency={preview.customer.currency}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                ) : null}

                {preview.unbillable.length > 0 ? (
                  <CardBody>
                    <Alert
                      tone="warning"
                      title={`${formatHours(unbillableHours)} timer i perioden kan ikke faktureres`}
                    >
                      <ul className="mt-1 space-y-0.5">
                        {Object.entries(
                          preview.unbillable.reduce<Record<string, number>>((groups, item) => {
                            groups[item.reason] = (groups[item.reason] ?? 0) + item.duration_hours;
                            return groups;
                          }, {}),
                        ).map(([reason, hours]) => (
                          <li key={reason}>
                            {UNBILLABLE_REASON_TEXT[reason as keyof typeof UNBILLABLE_REASON_TEXT]}:{' '}
                            {formatHours(round2(hours))} timer
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  </CardBody>
                ) : null}
              </>
            ) : null}
          </Card>

          {/*
            Vises saa snart der er et preview - ogsaa uden linjer og med blockere.
            Gem-knappen skal kunne bruges under afproevningen af flowet, og et kort der
            forsvandt, naar grundlaget var tomt, gjorde det umuligt at komme videre. Se
            kommentaren ved knappen.
          */}
          {preview ? (
            <Card>
              <CardHeader>
                <CardTitle>3. Bekræft</CardTitle>
              </CardHeader>
              <CardBody className="space-y-4">
                <dl className="space-y-1 text-sm">
                  <SummaryRow label="Kunde" value={preview.customer.name} />
                  {/*
                    Aftalerne staar med - alle af dem, og "Uden kontrakt" naar gruppen er med.
                    Det er den ene raekke, der siger hvad fakturaen daekker.
                  */}
                  <SummaryRow
                    label={preview.contracts.length > 1 ? 'Kontrakter' : 'Kontrakt'}
                    value={
                      [
                        ...new Set([
                          ...preview.contracts.map((row) => row.contract_no ?? row.title),
                          /*
                           * A contract with application operations and no hours is on it too. A
                           * subscription created in Codepass has no contract.
                           */
                          ...operationsLines.flatMap((line) =>
                            line.contract ? [line.contract.contract_no ?? line.contract.title] : [],
                          ),
                        ]),
                        ...(preview.lines.some((line) => line.contract === null)
                          ? [WITHOUT_CONTRACT_LABEL]
                          : []),
                      ].join(', ') || '—'
                    }
                  />
                  <SummaryRow
                    label="Periode"
                    value={
                      periodStart ? `${formatDate(periodStart)} – ${formatDate(periodEnd)}` : '—'
                    }
                  />
                  {/*
                    De valgte linjer, ikke alle linjer i grundlaget. Fravaelger man alle opgaver
                    paa en linje, er den ikke laengere en fakturalinje - og tallet skal svare til
                    det, der bliver gemt.
                  */}
                  <SummaryRow
                    label="Antal linjer"
                    value={String((retainer ? 1 : chosenLines.length) + operationsLines.length)}
                  />
                  <SummaryRow
                    label="Beløb ekskl. moms"
                    value={
                      <Money
                        value={preview.totals.amount_excl_vat}
                        currency={preview.customer.currency}
                      />
                    }
                  />
                  <SummaryRow
                    label={
                      preview.totals.reverse_charge
                        ? 'Moms (reverse charge)'
                        : `Moms (${preview.totals.vat_rate} %)`
                    }
                    value={
                      <Money
                        value={preview.totals.vat_amount}
                        currency={preview.customer.currency}
                      />
                    }
                  />
                  <SummaryRow
                    label="Beløb inkl. moms"
                    value={
                      <Money
                        value={preview.totals.amount_incl_vat}
                        currency={preview.customer.currency}
                        className="font-semibold"
                      />
                    }
                  />
                  <SummaryRow label="Forfaldsdato" value={formatDate(preview.due_date)} />
                </dl>

                {preview.totals.reverse_charge ? (
                  <Alert tone="info">
                    Kunden er ikke dansk. Momsen beregnes ikke, og fakturalinjerne påføres teksten
                    om reverse charge ved afsendelse til Business Central.
                  </Alert>
                ) : null}

                {create.error ? (
                  <Alert tone="error">
                    {create.error instanceof ApiError
                      ? create.error.message
                      : 'Kunne ikke oprette fakturaudkastet.'}
                  </Alert>
                ) : null}

                {/*
                  Knappen er slaaet fra naar der ikke er en eneste valgt linje - og **kun** da.

                  Blockere slaar den derimod ikke fra. Det er et bevidst afvig under
                  afproevningen: serveren gemmer udkastet uanset forbeholdene, saa resten af
                  arbejdsgangen - godkend, send, annullér - kan koeres igennem paa rigtige data,
                  foer valideringen strammes. Forbeholdene staar stadig oeverst paa siden og
                  foelger med paa udkastet.

                  Et tomt grundlag er ikke et forbehold, men en umulighed: serveren svarer 422
                  ("Grundlaget har ingen linjer"), fordi der ikke er noget at oprette i Business
                  Central. Knappen siger derfor det samme som svaret, frem for at lade
                  controlleren trykke og faa en fejl tilbage.

                  Naar Codepass-skemaet er rullet ud, skal blockerne ogsaa slaa den fra: der er
                  invariant 1 og 2 haandhaevet af unikke indexes, og et blokeret udkast maa
                  ikke kunne oprettes.
                */}
                {/*
                  `create.isPending` staar med i `disabled`, ikke kun i `loading`. Button
                  udleder ellers selv `disabled ?? loading`, og et udtrykkeligt `false` her
                  ville derfor ophaeve den udledning - saa knappen kunne trykkes igen, mens
                  oprettelsen var undervejs.
                */}
                {/*
                  Og slået fra, mens grundlaget beregnes forfra. Tallene ovenfor er da de forrige
                  - se `placeholderData` - og en kladde gemt i det øjeblik ville bære andre tal
                  end dem, controlleren kiggede på.
                */}
                <Button
                  loading={create.isPending}
                  disabled={create.isPending || recalculating || !canInvoice}
                  onClick={() => create.mutate()}
                >
                  Gem som kladde
                </Button>

                {recalculating ? (
                  <p className="text-xs text-ink-muted">
                    Grundlaget beregnes med dine ændringer. Knappen kan bruges, når tallene er
                    opdateret.
                  </p>
                ) : chosenLines.length === 0 && operationsLines.length === 0 && !retainer ? (
                  <p className="text-xs text-ink-muted">
                    Der er ingen valgte linjer, så der er ingenting at fakturere. Vælg mindst én
                    opgave under grundlaget.
                  </p>
                ) : !canInvoice ? (
                  /*
                    Linjer, men ingen regning. Alt i perioden er gratis eller inden for det
                    aftalte scope, og en faktura på nul kroner hører ikke i BC. Beskeden siger
                    hvorfor, så perioden eller klassifikationen kan rettes — frem for en knap,
                    der er grå uden grund.
                  */
                  <p className="text-xs text-ink-muted">
                    Der er ingenting at opkræve: alle timer er enten gratis eller inden for det
                    aftalte scope. En ændringsanmodning skal være klassificeret «Uden for scope» i
                    Orchestrator for at kunne faktureres.
                  </p>
                ) : preview.blockers.length > 0 ? (
                  <p className="text-xs text-ink-muted">
                    Udkastet gemmes trods forbeholdene ovenfor. De følger med på udkastet.
                  </p>
                ) : null}
              </CardBody>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * Opgaverne med status 'Sales' eller 'Not Billable' paa fakturaens aftaler - til opslag, under
 * Grundlag.
 *
 * Arbejde paa aftalens tavle, som nogen i OnePro har besluttet ikke skal opkraeves. Intet herfra
 * kommer paa fakturaen; tabellen findes, saa beslutningen kan ses, foer fakturaen gemmes.
 *
 * **Egen periode**, uafhaengig af fakturaens, og afgraenset paa opgavens faerdigdato ligesom
 * grundlaget. Som standard viser den **alt**: 'Fra' er den foerste opgaves dato, og 'Til' er
 * sidste dag i den aktuelle maaned.
 */
function UnbilledReviewCard({ contractNos, loading }: { contractNos: string; loading: boolean }) {
  const defaultEnd = useMemo(() => monthBounds(new Date()).end, []);
  /*
   * Null er "fra den foerste opgave" og ikke en dato, der mangler: serveren sender da alt fra
   * begyndelsen og siger, hvilken dato det er - `first_completed_date` - som feltet viser. En
   * dato her er controllerens eget valg; ryddes feltet, er standarden tilbage.
   */
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState(defaultEnd);

  const validPeriod = Boolean(end) && (start === null || start <= end);

  const { data, isFetching, error } = useQuery({
    queryKey: ['invoice-unbilled-tasks', contractNos, start, end],
    queryFn: () =>
      api.get<UnbilledReviewTasksResponse>('/invoices/unbilled-tasks', {
        contract_nos: contractNos,
        period_start: start,
        period_end: end,
      }),
    enabled: Boolean(contractNos) && validPeriod,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ikke med på fakturaen</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-ink-muted">
          Opgaver med status Salg eller Ikke fakturerbar på fakturaens aftaler. Til opslag — de
          kommer ikke på fakturaen.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="unbilled-from">Fra</Label>
            <Input
              id="unbilled-from"
              type="date"
              value={start ?? data?.first_completed_date ?? ''}
              onChange={(event) => setStart(event.target.value || null)}
            />
          </div>
          <div>
            <Label htmlFor="unbilled-to">Til</Label>
            <Input
              id="unbilled-to"
              type="date"
              value={end}
              onChange={(event) => setEnd(event.target.value)}
            />
          </div>
        </div>

        {!validPeriod ? (
          <p className="text-xs text-ink-muted">
            Vælg en periode, hvor fra-datoen er før til-datoen.
          </p>
        ) : loading ? (
          <Skeleton className="h-12" />
        ) : !contractNos ? (
          <p className="text-xs text-ink-muted">
            Fakturaen har ingen aftaler med et kontraktnummer, så der er ingen opgaver at vise.
          </p>
        ) : isFetching && !data ? (
          <Skeleton className="h-12" />
        ) : error ? (
          <Alert tone="error">
            {error instanceof ApiError ? error.message : 'Kunne ikke hente opgaverne.'}
          </Alert>
        ) : data && data.data.length === 0 ? (
          <p className="text-xs text-ink-muted">
            Ingen opgaver med status Salg eller Ikke fakturerbar i perioden.
          </p>
        ) : data ? (
          /* Loftet paa hoejden: en aftale kan have hundreder af opgaver, og kortet staar ved siden af gennemgangen. */
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-line text-ink-muted">
                  <th className="py-1 text-left font-medium">Opgave</th>
                  <th className="py-1 text-left font-medium">Status</th>
                  <th className="w-[56px] py-1 text-right font-medium">Timer</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((task) => (
                  <tr key={task.id} className="border-b border-line/60 align-top">
                    <td className="py-1 pr-2">
                      <span className="block">{task.title}</span>
                      <span className="block text-[11px] text-ink-muted">
                        {[task.contract_no, formatDate(task.completed_date), task.consultant_name]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </td>
                    <td className="py-1 pr-2">
                      <TimeEntryStatusBadge status={task.status} />
                    </td>
                    <td className="py-1 text-right tabular">{formatHours(task.hours)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} className="py-1 font-medium">
                    I alt ({data.data.length})
                  </td>
                  <td className="py-1 text-right font-medium tabular">
                    {formatHours(data.total_hours)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : null}

        {/* Uden faerdigdato kan de ikke vaelges frem med perioden - de naevnes frem for at mangle. */}
        {data && data.without_completion > 0 ? (
          <p className="text-xs text-ink-muted">
            {data.without_completion} {data.without_completion === 1 ? 'opgave har' : 'opgaver har'}{' '}
            en af statusserne, men ingen færdigdato, og kan ikke vises i en periode.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

/**
 * Bufferen pr. aftale under feltet: målet, det opsamlede, og hvad denne faktura tager.
 *
 * Tallene er serverens (`planInvoiceBuffer`). 'Ønsket' er andelen af det manglende - eller den
 * skrevne andel - og 'Lagt på' er det, opgaverne havde plads til under deres estimater.
 */
function BufferPlanTable({
  plans,
  mode,
  currency,
}: {
  plans: ContractBufferPlan[];
  mode: 'auto' | 'manual';
  currency: string;
}) {
  const rows = plans.filter((plan) => plan.contract || plan.applied_excl_vat > 0);

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-line text-ink-muted">
          <th className="py-1 text-left font-medium">Aftale</th>
          <th className="py-1 text-right font-medium">Samlet buffer</th>
          <th className="py-1 text-right font-medium">Opsamlet</th>
          <th className="py-1 text-right font-medium">{mode === 'auto' ? 'Andel' : 'Ønsket'}</th>
          <th className="py-1 text-right font-medium">Lagt på</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((plan) => (
          <tr key={plan.contract?.id ?? 'none'} className="border-b border-line/60 align-top">
            <td className="py-1">
              {plan.contract?.contract_no ?? plan.contract?.title ?? WITHOUT_CONTRACT_LABEL}
              {plan.limited_by ? (
                <span className="block text-amber-700">
                  {plan.limited_by === 'contract_cap'
                    ? 'Aftalens timeloft er nået'
                    : 'Opgaverne har ikke mere plads under deres estimat'}
                </span>
              ) : mode === 'auto' && plan.contract && plan.target_excl_vat === 0 ? (
                <span className="block text-ink-muted">Ingen samlet buffer på aftalen</span>
              ) : null}
            </td>
            <td className="py-1 text-right">
              {plan.contract ? (
                <Money value={plan.target_excl_vat} currency={currency} decimals={0} />
              ) : (
                '—'
              )}
            </td>
            <td className="py-1 text-right">
              {plan.contract ? (
                <Money value={plan.collected_excl_vat} currency={currency} decimals={0} />
              ) : (
                '—'
              )}
            </td>
            <td className="py-1 text-right">
              <Money value={plan.wanted_excl_vat} currency={currency} />
            </td>
            <td className="py-1 text-right font-medium">
              <Money value={plan.applied_excl_vat} currency={currency} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
